import type { CarbonIconType } from '@carbon/icons-react';
import { useTheme } from '@repo/theme';
import { UtilityText } from 'component/text';
import type { ReactNode } from 'react';
import { StyleSheet, View } from 'react-native';
import type { FormatParams, TLabel } from 'util/stdtypes';
import { invariant } from 'util/util';

type BaseTagBorder = 'solid' | 'dashed';

type BaseProps = {
    color?: string;
    label?: TLabel;
    text?: string;
    /**
     * icon or emoji before the label/text (CarbonIconType for icon, string for
     * emoji)
     */
    prefix?: CarbonIconType | string;
    formatParams?: FormatParams;
};

type VisibleTagProps = BaseProps & {
    type: 'big' | 'small';
    backgroundColor?: string;
    iconFill?: string;
    border?: BaseTagBorder;
    /** Use uppercase text with letter spacing */
    caps?: boolean;
};

export type BaseTagProps = VisibleTagProps;

function renderIconOrEmoji(item?: CarbonIconType | string, color?: string): ReactNode {
    if (!item) return null;
    if (typeof item === 'string') return <UtilityText text={item} type='tiny' weight='medium' />;
    const Icon = item;
    return <Icon size={16} style={{ color }} />;
}

// Figma:
// https://www.figma.com/design/5vqFJlKdA6UAQJeXYlMQW2/PSI-Stage-2?node-id=19746-33920
export function BaseTag(props: BaseTagProps) {
    const { colors } = useTheme();
    const { type, label, text, prefix, formatParams } = props;

    invariant(!!text !== !!label, 'Tags must have either label or text, but not both.');

    const backgroundColor = props.backgroundColor ?? colors.colorTagDefault;
    const iconFill = props.iconFill ?? colors.colorTextPrimary;
    const textColor = props.color ?? colors.colorTextPrimary;
    const border = props.border;
    const caps = props.caps;

    const xPadding = type === 'big' ? 12 : 6;
    const height = type === 'big' ? 28 : 20;

    const containerStyle = [
        styles.base,
        {
            backgroundColor,
            height,
            paddingHorizontal: xPadding,
        },
        border && {
            borderColor: colors.colorBorderPrimary,
            borderStyle: border,
            borderWidth: 1,
        },
    ];

    return (
        <View style={containerStyle}>
            {prefix && <View style={styles.iconContainer}>{renderIconOrEmoji(prefix, iconFill)}</View>}
            <UtilityText caps={caps} color={textColor} formatParams={formatParams} label={label} text={text} type='tiny' weight='medium' />
        </View>
    );
}

const styles = StyleSheet.create({
    base: {
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'center',
        borderRadius: 32,
        alignSelf: 'flex-start',
        gap: 6,
    },
    iconContainer: {
        width: 12,
        alignItems: 'center',
        justifyContent: 'center',
    },
});
